import moment from 'moment';
import { adviserFactsheetOf, qualifyingFactsheetOf } from '../../../utils/client-factsheet';
import type { ClientTag, QueueClient } from '../../../types/client';

// react-intl 2.9 ships no type declarations, so we depend on a minimal
// structural type for the only method these helpers use.
export type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  /** react-intl's locale; picks the language for the names in constants/disc. */
  locale?: string;
};

export type ClientIdentity = {
  /** Rendered first. The nickname when there is no name, mirroring V1. */
  primary: string;
  /** Rendered as a ", …" suffix. Empty unless a name came first. */
  secondary: string;
  /** `primary, secondary` — for aria-labels and the 姓名 A–Z sort. */
  display: string;
};

/**
 * Identity exactly as the V1 row renders it (ClientEntry.js:607-608, 736-737):
 *
 *   const factsheet = get(client, 'factsheetId', {});
 *   const name      = get(factsheet, 'name', '');
 *   const nickname  = get(factsheet, 'nickname', '');
 *   `${name}${name && nickname && ', '}` + {nickname && <Grid>{nickname}</Grid>}
 *
 * So: both -> "name, nickname"; name only -> "name"; nickname only -> the
 * nickname alone. The nickname is NOT de-duplicated — a client whose name and
 * nickname are both "www" renders "www, www" in V1, and does here too.
 *
 * The factsheet is the only source, and no client-side adviser gate applies —
 * this is NOT an exception to Established Pattern #2, it is what the data model
 * dictates:
 *
 *  - `client.factsheetId` is ADVISER-owned and the server has already scoped it
 *    to the requesting adviser: clientController.getClient resolves it via
 *    `getClientFactsheetId(client, adviser)` (utils/client.js — the inactive
 *    client's own factsheet, otherwise the matching advisorDetails entry's) and
 *    assigns that document to the root `factsheetId`. There is nothing left to
 *    gate.
 *  - `client.name` is CLIENT-owned and deliberately withheld: formatClientData
 *    only sets it inside `if (dataAccess)`, i.e. inactive or connected. On the
 *    live 374-client list 332 carry it — the missing 42 are exactly the
 *    unconnected clients, and reading the factsheet is the only way to name
 *    them. (formatClientData even has the factsheet->name copy commented out,
 *    leaving this resolution to the client.)
 *
 * Verified against that live list: this reproduces V1's rendered string for
 * every one of the 374 clients, 0 mismatches.
 */
export const clientIdentityOf = (client: QueueClient): ClientIdentity => {
  const name = client.factsheetId?.name || '';
  const nickname = client.factsheetId?.nickname || '';

  const primary = name || nickname;
  const secondary = name ? nickname : '';
  return { primary, secondary, display: secondary ? `${primary}, ${secondary}` : primary };
};

/** Single-string identity, for sorting and any one-line label. */
export const clientNameOf = (client: QueueClient): string => clientIdentityOf(client).display;

// MIRROR — keep in sync with the canonical sources; values are re-declared here
// (not imported) on purpose: the canonical constants live in
// components/ClientEntry/ClientEntry.js, whose module graph pulls the whole
// adviser store + native Capacitor plugins, which would bloat this leaf's
// bundle and break its jest run. The logic below reproduces the real helpers
// exactly (getCPIScoreColor, getLastMeetingColorBar, TRACKING_COLORS), and
// PART 6h/6p of the handoff tracks this as a mirror.
//   ClientEntry.js:  BORDER_COLORS = ['#5EE092', '#FFAF19', '#FF6A74', '#cccccc'];
//   constants/analysis.js:  SCORE_LEVEL_COLORS = ['#ff0000', '#ffa600', '#3eda7d', '#3999f8'];
//   ClientEntry.js:  TRACKING_COLORS = { L: BORDER_COLORS[2], M: BORDER_COLORS[1], H: BORDER_COLORS[0], NA: BORDER_COLORS[3] };
//   ClientEntry.js:97  DISC_COLORS = { D: '#EA8462', I: '#EAC862', S: '#62EA84', C: '#62A6EA' };
const BORDER_COLORS = ['#5EE092', '#FFAF19', '#FF6A74', '#cccccc'];
const SCORE_LEVEL_COLORS = ['#ff0000', '#ffa600', '#3eda7d', '#3999f8'];
const TRACKING_COLORS: Record<string, string> = {
  L: BORDER_COLORS[2],
  M: BORDER_COLORS[1],
  H: BORDER_COLORS[0],
  NA: BORDER_COLORS[3]
};
// BORDER_COLORS are pastel — fine for a border/dot/12%-alpha fill, too washed
// out as text on that same near-white fill. Darkened same-hue shades, text
// only; border/dot/fill keep the pastel from TRACKING_COLORS.
const TRACKING_TEXT_COLORS: Record<string, string> = {
  L: '#C62838',
  M: '#8A5700',
  H: '#1E8449',
  NA: '#64748B'
};
const DISC_COLORS: Record<string, string> = { D: '#EA8462', I: '#EAC862', S: '#62EA84', C: '#62A6EA' };

/** Avatar colour = the adviser's own factsheet gender (handoff PART 6p).
 * `client.gender` remains a compatibility fallback for older API shapes; the
 * server only sends it while the relationship is live, so it self-gates. */
export const avatarGenderClass = (client: QueueClient, adviserEmail?: string): 'male' | 'female' | 'unspecified' => {
  const gender = adviserFactsheetOf(client, adviserEmail)?.gender || client.gender;
  if (gender === 'Male') return 'male';
  if (gender === 'Female') return 'female';
  return 'unspecified';
};

export type CpiInfo = { wording: string; color: string };

// CPI pill. Mirrors getCPIScoreColor: colour = BORDER_COLORS[scoreLevel] for a
// valid score, else the "no data" grey. Wording is Chinese-capable via i18n
// keyed by scoreLevel. Returns null when there is no valid score, so the caller
// hides the pill entirely (v35).
export const cpiOf = (client: QueueClient, intl: IntlLike): CpiInfo | null => {
  const level = Number.parseInt(String(client.scoreLevel), 10);
  if (!client.validScore || Number.isNaN(level) || level > 2) return null;
  const id = level === 0 ? 'clientv2-cpi-high' : level === 1 ? 'clientv2-cpi-good' : 'clientv2-cpi-fair';
  return { wording: intl.formatMessage({ id }), color: BORDER_COLORS[level] };
};

/**
 * Colour of the adviser-level client-base indicator, mirroring
 * clientPage.js:1188-1193 — `BORDER_COLORS[clientBaseLevel]`, falling back to
 * the "no data" grey when the server sent no level (fewer valid clients than
 * CPI_LEAST_VALID_CLIENTS, or no platform Figure boundaries yet).
 */
export const clientBaseColorOf = (level: number | null | undefined): string =>
  typeof level === 'number' && level >= 0 && level <= 2 ? BORDER_COLORS[level] : BORDER_COLORS[3];

/** Soft fill behind each band's colour, for the chip and the legend rows. */
const CLIENT_BASE_TINTS = ['#e7fbef', '#fff5e2', '#ffecee', '#f1f5f9'];

// V1's own wording for the four bands (cpiDetailPopup's `clientBaseWordings`),
// except the three live ones reuse the v46 `clientv2-cpi-*` ids the client pills
// already use — 高 / 良好 / 一般 are the same three words either way, and one
// source for them means the pill and the indicator can never disagree.
const CLIENT_BASE_LABEL_KEYS = ['clientv2-cpi-high', 'clientv2-cpi-good', 'clientv2-cpi-fair', 'No Data'];

export type ClientBaseBand = {
  /** 0 high / 1 good / 2 fair / 3 no data — always resolved, never null. */
  level: number;
  wording: string;
  color: string;
  tint: string;
};

/**
 * The four bands, resolved for display. Both the indicator in the tab row and
 * the explainer's legend read this, so the colour a band shows in one is the
 * colour it shows in the other by construction.
 */
export const clientBaseBands = (intl: IntlLike): ClientBaseBand[] =>
  CLIENT_BASE_LABEL_KEYS.map((id, level) => ({
    level,
    wording: intl.formatMessage({ id }),
    color: BORDER_COLORS[level],
    tint: CLIENT_BASE_TINTS[level]
  }));

/** The band the server put this adviser in; the grey 資料不足 band when none. */
export const clientBaseBandOf = (level: number | null | undefined, intl: IntlLike): ClientBaseBand => {
  const bands = clientBaseBands(intl);
  return typeof level === 'number' && level >= 0 && level <= 2 ? bands[level] : bands[3];
};

export type ElapsedInfo = { label: string; color: string };

// Mirrors getLastMeetingColorBar's thresholds (<6 / <12 / <18 / else months).
const elapsedColor = (months: number): string => {
  if (months < 6) return SCORE_LEVEL_COLORS[3];
  if (months < 12) return SCORE_LEVEL_COLORS[2];
  if (months < 18) return SCORE_LEVEL_COLORS[1];
  return SCORE_LEVEL_COLORS[0];
};

// "Time since last meeting" dot + label. One of the few factsheet reads V1
// really does gate on the connection (`getLastMeetingDate`,
// components/ClientEntry/utils.js:101), so a disconnected relationship yields
// none — unlike tier/tags/family beside it. Label drops the redundant 「未見」 and
// switches to years past a year (handoff PART 6d). Returns null when there is
// no record, so the caller omits the chip.
export const elapsedOf = (client: QueueClient, intl: IntlLike, adviserEmail?: string): ElapsedInfo | null => {
  const date = qualifyingFactsheetOf(client, adviserEmail)?.latestMeetingDate;
  if (!date) return null;
  const months = Math.max(0, moment().diff(moment(date), 'months'));

  let label: string;
  if (months < 1) {
    label = intl.formatMessage({ id: 'clientv2-lt-month' });
  } else if (months < 12) {
    label = `${months} ${intl.formatMessage({ id: 'Mth' })}`;
  } else {
    label = `${Math.round((months / 12) * 10) / 10} ${intl.formatMessage({ id: 'Years' })}`;
  }
  return { label, color: elapsedColor(months) };
};

export type LastMeetingDuration = {
  months: number;
  days: number;
  label: string;
};

// Exact month/day duration used by the v46 LastMeetingHint mirror. This is kept
// separate from elapsedOf's compact list label (which switches to years).
export const lastMeetingDurationOf = (
  client: QueueClient,
  intl: IntlLike,
  adviserEmail?: string,
  now?: moment.MomentInput
): LastMeetingDuration | null => {
  const date = qualifyingFactsheetOf(client, adviserEmail)?.latestMeetingDate;
  if (!date) return null;
  const parsed = moment(date, 'YYYY-MM-DD');
  if (!parsed.isValid()) return null;

  const difference = moment(now).diff(parsed);
  const duration = moment.duration(difference);
  const future = difference < 0;
  const months = future ? Math.ceil(duration.asMonths()) : Math.floor(duration.asMonths());
  const days = future
    ? Math.ceil(duration.clone().add(months, 'month').asDays())
    : Math.floor(duration.clone().subtract(months, 'month').asDays());
  const label =
    months === 0 && days === 0
      ? `0 ${intl.formatMessage({ id: 'daycount' })}`
      : `${months} ${intl.formatMessage({ id: 'Mth' })} ${days} ${intl.formatMessage({ id: 'daycount' })}`;
  return { months, days, label };
};

export type TrackingInfo = { color: string; textColor: string };

// The 跟進中 chip; only present when tracking is active. Colour = opportunity
// level via TRACKING_COLORS (handoff PART 6o supersedes the old right-edge bar).
export const trackingOf = (client: QueueClient): TrackingInfo | null => {
  const tracking = client.trackingId;
  if (!tracking || !tracking.isActive) return null;
  const key = tracking.opportunity || 'NA';
  return {
    color: TRACKING_COLORS[key] || TRACKING_COLORS.NA,
    textColor: TRACKING_TEXT_COLORS[key] || TRACKING_TEXT_COLORS.NA
  };
};

// Opportunity levels shown as the 機率 L/M/H buttons in the drawer's tracking
// detail row, ordered low→high (mirrors ClientEntry OpportunityControl).
export const OPPORTUNITY_LEVELS = ['L', 'M', 'H'] as const;
export type OpportunityLevel = (typeof OPPORTUNITY_LEVELS)[number];

export type TrackingDetail = {
  /** 'L' | 'M' | 'H' | 'NA' — the currently selected opportunity. */
  opportunity: string;
  met: number;
  businessVol: number | null;
  /** Days since tracking started (from startDate); 0 when unknown. */
  days: number;
  /** Fill colour for the selected 機率 button + the row tint (TRACKING_COLORS). */
  color: string;
  /**
   * Colour for any band, including none — the drawer tints itself from the
   * band the adviser has just tapped, before the store has caught up, so this
   * has to answer for `null` too.
   */
  colorFor: (level: string | null | undefined) => string;
};

/**
 * 預期生意, grouped in thousands — 40988 is a sum of money and reads as one at
 * 40,988, not as a five-digit run the adviser has to count. Grouped with commas
 * in every locale this app ships (en / zh / zh-Hant-HK all use them for money),
 * so the separator is fixed rather than taken from the browser's own locale,
 * which would otherwise turn it into 40.988 for a European visitor.
 *
 * The value arrives as whatever the tracking record holds — a number, or a
 * string the adviser typed. Anything that is not a number comes back untouched
 * rather than as NaN; an amount nobody can parse is still an amount, and the
 * adviser is the one who can fix it.
 */
export const formatBusinessVolume = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  if (!Number.isFinite(numeric)) return String(value);
  return numeric.toLocaleString('en-US');
};

// The drawer's tracking detail row (機率 / 預期生意 / 天數 / 會面). Only present
// when tracking is active - same gate as trackingOf, just the fuller shape the
// detail row needs. `days` is elapsed since startDate (ClientEntry shows the
// same 天數 count); `colorFor` gives each L/M/H button its highlight colour so
// only the selected one is tinted (the rest render neutral grey), matching the
// preview's OpportunityControl mirror.
export const trackingDetailOf = (client: QueueClient): TrackingDetail | null => {
  const tracking = client.trackingId;
  if (!tracking || !tracking.isActive) return null;
  const opportunity = tracking.opportunity || 'NA';
  const start = tracking.startDate ? moment(tracking.startDate) : null;
  const days = start && start.isValid() ? Math.max(0, moment().diff(start, 'days')) : 0;
  return {
    opportunity,
    met: tracking.met ?? 0,
    businessVol: tracking.businessVol ?? null,
    days,
    color: TRACKING_COLORS[opportunity] || TRACKING_COLORS.NA,
    colorFor: level => (level ? TRACKING_COLORS[level] : undefined) || TRACKING_COLORS.NA
  };
};

export type ConnectionStatus = 'Connected' | 'Pending' | 'Inactive' | 'Disconnected';
export type ConnectionMeta = {
  status: ConnectionStatus;
  /** i18n message id — the four status words already exist in the locale files. */
  key: ConnectionStatus;
  /** CSS modifier (connected/pending/inactive/disconnected). */
  cls: string;
};

// Any adviser on the client is connected. Mirrors getClientConnection(client)
// with no userDetails (ClientEntry utils.js), which finds ANY connected
// advisorDetails entry - the connection pill reflects the account link, which
// is not per-adviser, so this is deliberately not gated by adviserEmail.
const hasConnectedAdviser = (client: QueueClient): boolean =>
  Boolean(client.advisorDetails?.some(detail => detail.connection));

// Connection-status pill in the drawer header. Mirrors ClientEntry utils.js
// isClientDisconnected + getClientConnectionStatus exactly (see the MIRROR note
// above): disconnected unless linked/inactive/mid-reset; an inactive user is
// Pending once a connection request exists, else Inactive (未開戶); an active
// user is Pending mid password-reset, else Connected.
export const connectionStatusOf = (client: QueueClient): ConnectionMeta => {
  const disconnected = !(client.passwordChangeKey || client.inactiveUser || hasConnectedAdviser(client));
  let status: ConnectionStatus;
  if (disconnected) {
    status = 'Disconnected';
  } else if (client.inactiveUser) {
    status = client.connectionRequestId ? 'Pending' : 'Inactive';
  } else {
    status = client.passwordChangeKey ? 'Pending' : 'Connected';
  }
  return { status, key: status, cls: status.toLowerCase() };
};

export type PersonalityMeta = {
  /** DISC letter 'D' | 'I' | 'S' | 'C'. */
  letter: string;
  /** DISC_COLORS[letter] - the saturated hue; the badge blends a pastel of it. */
  color: string;
};

// DISC badge in the drawer's 客戶檔案 section. Adviser-owned, so it survives a
// disconnection; returns null for an unset/unknown letter so
// the caller omits the row. Reuses the existing DISC-short-{D,I,S,C} message keys
// for the description rather than re-hardcoding the English/Chinese wording.
/**
 * A DISC letter's colour, for callers holding a letter rather than a client —
 * 補資料's DISC question, where the letter on screen is whatever the adviser has
 * tapped so far and may not be the one on file yet. Same table
 * `personalityMetaOf` reads, so the mark in the card and the mark in the panel's
 * DISC cell can never disagree.
 *
 * Falls back to the "no data" grey the C.P.I. pill uses, so an unanswered DISC
 * still gets a mark rather than a hole.
 */
export const discColorOf = (letter: string | null | undefined): string =>
  (letter && DISC_COLORS[letter]) || BORDER_COLORS[3];

export const personalityMetaOf = (client: QueueClient, adviserEmail?: string): PersonalityMeta | null => {
  const letter = adviserFactsheetOf(client, adviserEmail)?.personality;
  if (!letter || !DISC_COLORS[letter]) return null;
  return { letter, color: DISC_COLORS[letter] };
};

// NOTE: a `meetingReminderActiveOf` here answered "is a reminder set" as a bare
// boolean, for the animated red dot on the 會面筆記 tile. That dot could never
// render (the card carries V1's permanent badge instead, which suppressed it),
// and `reminderOf` below reads the same two gated fields and returns the date
// and the countdown as well — which is what the 提醒 cell shows now. One reader
// of fsReminderActive, not two.

export type ReminderMeta = {
  /** The date itself, ready to print. */
  date: moment.Moment;
  /** Whole days from the start of `now`: 0 is today, negative is overdue. */
  days: number;
};

/**
 * The 提醒我 date the drawer counts down to.
 *
 * Same two conditions and the same parse format the today-queue's own reminder
 * signal uses (utils/client-queue.ts detectSignal) — `fsReminderActive` AND a
 * `fsReminderDate` read as 'YYYY-MM-DD' — so the drawer's countdown and the
 * queue's "remind today" entry can never disagree about whether a reminder
 * exists. Read through the gated factsheet for the same reason
 * meetingReminderActiveOf is: this is the signal V1's `isClientRemindMe` gates
 * on the connection, not an ordinary factsheet field.
 *
 * A date already past is still returned, with a negative `days`. Hiding a
 * reminder the adviser missed is the one thing a reminder must never do.
 */
export const reminderOf = (
  client: QueueClient,
  adviserEmail: string | undefined,
  now: moment.Moment
): ReminderMeta | null => {
  const factsheet = qualifyingFactsheetOf(client, adviserEmail);
  if (!factsheet?.fsReminderActive || !factsheet.fsReminderDate) return null;
  const date = moment(factsheet.fsReminderDate, 'YYYY-MM-DD');
  if (!date.isValid()) return null;
  return { date, days: date.diff(now.clone().startOf('day'), 'days') };
};

/**
 * The reminder mark that rides on the avatar — a symbol, not a date.
 *
 * `due` is today or already past (act now), `upcoming` is anything still ahead.
 * Both are worth a mark: the point of the badge is "this client has something
 * booked", which is exactly what a list of names cannot otherwise say. The date
 * itself stays in the drawer's 提醒 cell, where there is room to print it.
 *
 * `now` defaults to the real clock so a list row does not have to be given one —
 * `elapsedOf` above reads the clock the same way. Callers that already hold a
 * reference time (the drawer) pass it, so the avatar and the 提醒 cell beneath
 * it can never disagree.
 */
export type ReminderMark = 'due' | 'upcoming';

export const reminderMarkOf = (
  client: QueueClient,
  adviserEmail?: string,
  now: moment.Moment = moment()
): ReminderMark | null => {
  const reminder = reminderOf(client, adviserEmail, now);
  if (!reminder) return null;
  return reminder.days <= 0 ? 'due' : 'upcoming';
};

// Family names. Adviser-owned like tier and tags, so a disconnection does not
// take them away — V1 reads them straight off the factsheet (client-factsheet.ts).
export const familiesOf = (client: QueueClient, adviserEmail?: string): string[] =>
  adviserFactsheetOf(client, adviserEmail)?.families || [];

export const isRecruitmentOf = (client: QueueClient, adviserEmail?: string): boolean =>
  Boolean(adviserFactsheetOf(client, adviserEmail)?.isRecruitment);

/**
 * 現有客戶 — 'Yes' / 'No' / null, the same tri-state the factsheet form writes
 * (personalDetailsForm.js:1529-1549) and the 現有客戶 filter reads.
 *
 * It used to return `Boolean(factsheet.existingClient)`, which called a client
 * who had explicitly answered 否 an existing client — `Boolean('No') === true`. The
 * drawer now offers the choice itself, so the two answers have to stay
 * distinguishable from each other and from "never answered".
 */
export type ExistingClientChoice = 'Yes' | 'No';

export const existingClientOf = (client: QueueClient, adviserEmail?: string): ExistingClientChoice | null => {
  const raw = adviserFactsheetOf(client, adviserEmail)?.existingClient;
  if (raw === 'Yes' || raw === 'No') return raw;
  return null;
};

// Tags with each tag's colour resolved the way V1 does it (getUsedTagColor):
// the adviser's usedTags palette wins, and the colour saved on the factsheet tag
// is only the fallback. The factsheet copy is a snapshot taken when the tag was
// added and goes stale — older rows carry a different hue and a translucent
// alpha suffix (#f73b6a50) — so reading it directly painted the same tag one
// colour in the drawer and another in the filter sidebar, which reads usedTags.
export const tagsOf = (client: QueueClient, adviserEmail?: string, usedTags?: ClientTag[]): ClientTag[] => {
  const tags = adviserFactsheetOf(client, adviserEmail)?.tags || [];
  if (!usedTags || usedTags.length === 0) return tags;
  return tags.map(tag => {
    const color = usedTags.find(used => used.text === tag.text)?.color;
    return color && color !== tag.color ? { ...tag, color } : tag;
  });
};

export type TierMeta = { cls: string; key: string };

// importance → visible tier badge. Adviser-owned; V1 reads it off the factsheet
// with no connection test (ClientEntry.js:617). Normal gets no badge — matches V1,
// which only shows an importance image for VIP/VVIP; a 普通 chip on every
// ordinary client is noise, not signal.
export const tierMetaOf = (client: QueueClient, adviserEmail?: string): TierMeta | null =>
  tierBadgeOf(tierValueOf(client, adviserEmail));

/**
 * The same badge, from a tier value rather than from a client.
 *
 * The drawer echoes a just-tapped 重要性 locally while the write is in flight,
 * and the chip beside the client's name has to follow that echo — reading the
 * client again there would show the old band until the server answered.
 */
export const tierBadgeOf = (tier: ClientTierChoice | null): TierMeta | null => {
  if (tier === 'V.VIP') return { cls: 'vvip', key: 'V.VIP' };
  if (tier === 'VIP') return { cls: 'vip', key: 'VIP' };
  return null;
};

/**
 * 重要性, as the factsheet form writes it (personalDetailsForm.js:1588-1625):
 * three named bands plus a cleared state, toggled off by tapping the active
 * one. `tierMetaOf` above deliberately answers null for 普通 — that is the
 * badge's question ("is this client worth a chip beside their name"), not this
 * one, which is "what is on file".
 */
export const CLIENT_TIER_CHOICES = ['Normal', 'VIP', 'V.VIP'] as const;

export type ClientTierChoice = (typeof CLIENT_TIER_CHOICES)[number];

export const tierValueOf = (client: QueueClient, adviserEmail?: string): ClientTierChoice | null => {
  const raw = adviserFactsheetOf(client, adviserEmail)?.importance;
  return CLIENT_TIER_CHOICES.find(choice => choice === raw) || null;
};
